import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Building2 } from "lucide-react";
import { api, fmtINR, fmtDate } from "../lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { FileText, FileDown } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stat = ({ label, value, cls }) => (
  <div className="bg-white border border-slate-200 rounded-sm p-4">
    <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</div>
    <div className={`text-xl font-mono tabular-nums font-semibold mt-1.5 ${cls || "text-slate-900"}`}>{value}</div>
  </div>
);

const Table = ({ head, rows, render, empty }) => (
  <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
    <table className="w-full text-sm">
      <thead><tr className="bg-slate-50 border-b border-slate-200 text-left">{head.map((h) => <th key={h} className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap">{h}</th>)}</tr></thead>
      <tbody>
        {rows.length === 0 ? <tr><td colSpan={head.length} className="px-4 py-8 text-center text-slate-400">{empty}</td></tr>
          : rows.map((r, i) => <tr key={i} className="border-b border-slate-100">{render(r)}</tr>)}
      </tbody>
    </table>
  </div>
);

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [stStart, setStStart] = useState("");
  const [stEnd, setStEnd] = useState("");

  useEffect(() => { api.get(`/customers/${id}/profile`).then((r) => setD(r.data)); }, [id]);
  if (!d) return <div className="text-slate-400 py-20 text-center">Loading...</div>;
  const c = d.customer;
  const openPdf = (id) => window.open(`${API}/pdf/invoice/${id}?token=${localStorage.getItem("token")}`, "_blank");
  const openStatement = () => {
    const qs = new URLSearchParams({ token: localStorage.getItem("token") });
    if (stStart) qs.set("start", stStart);
    if (stEnd) qs.set("end", stEnd);
    window.open(`${API}/customers/${id}/statement/pdf?${qs.toString()}`, "_blank");
  };
  const progressBadge = (p) => <Badge variant="outline" className={`rounded-sm ${p === "Completed" ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-amber-100 text-amber-700 border-amber-200"}`}>{p}</Badge>;

  return (
    <div>
      <button onClick={() => navigate("/customers")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-4"><ArrowLeft className="w-4 h-4" /> Back to Customers</button>
      <div className="flex items-start gap-3 mb-6">
        <div className="w-12 h-12 rounded-sm bg-[#0B132B] flex items-center justify-center"><Building2 className="w-6 h-6 text-white" /></div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-head">{c.company_name}</h1>
          <p className="text-sm text-slate-500">{c.contact_person} · {c.city}, {c.state} · {c.mobile}</p>
          <p className="text-xs text-slate-400 mt-0.5">GST: {c.gst_number || "-"} · Terms: {c.payment_terms || "-"}</p>
        </div>
        <div className="ml-auto flex items-end gap-2 flex-wrap">
          <div className="space-y-1"><label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">From</label>
            <Input data-testid="statement-start" type="date" value={stStart} onChange={(e) => setStStart(e.target.value)} className="h-9 w-36 bg-white" /></div>
          <div className="space-y-1"><label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">To</label>
            <Input data-testid="statement-end" type="date" value={stEnd} onChange={(e) => setStEnd(e.target.value)} className="h-9 w-36 bg-white" /></div>
          <Button data-testid="download-statement-btn" onClick={openStatement} className="bg-[#0B132B] hover:bg-[#1C2541] text-white rounded-md gap-1.5 h-9 transition-all active:scale-95">
            <FileDown className="w-4 h-4" /> Download Statement
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Stat label="Total Sales" value={fmtINR(d.total_sales)} />
        <Stat label="Total Received" value={fmtINR(d.total_received)} cls="text-emerald-600" />
        <Stat label="Outstanding" value={fmtINR(d.outstanding)} cls={d.outstanding > 0 ? "text-red-600" : "text-slate-900"} />
        <Stat label="Credit Limit" value={fmtINR(c.credit_limit)} />
      </div>

      <Tabs defaultValue="invoices">
        <TabsList className="bg-white border border-slate-200 rounded-sm">
          <TabsTrigger value="invoices" data-testid="tab-invoices">Invoices</TabsTrigger>
          <TabsTrigger value="orders" data-testid="tab-orders">Orders</TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments">Payment History</TabsTrigger>
          <TabsTrigger value="samples" data-testid="tab-samples">Sample History</TabsTrigger>
        </TabsList>
        <TabsContent value="invoices" className="mt-4">
          <Table head={["Invoice No", "Date", "Product / Items", "Qty", "Total", "Received", "Outstanding", "Status", "PDF"]} rows={d.invoices} empty="No invoices"
            render={(r) => (<>
              <td className="px-4 py-2.5 font-medium text-slate-800">{r.invoice_no}</td>
              <td className="px-4 py-2.5 whitespace-nowrap">{fmtDate(r.invoice_date)}</td>
              <td className="px-4 py-2.5">{r.products}</td>
              <td className="px-4 py-2.5 font-mono tabular-nums">{r.quantity}</td>
              <td className="px-4 py-2.5 font-mono tabular-nums">{fmtINR(r.total)}</td>
              <td className="px-4 py-2.5 font-mono tabular-nums text-emerald-600">{fmtINR(r.received)}</td>
              <td className="px-4 py-2.5 font-mono tabular-nums text-red-600">{fmtINR(r.outstanding)}</td>
              <td className="px-4 py-2.5"><Badge variant="outline" className="rounded-sm capitalize">{r.status}</Badge></td>
              <td className="px-4 py-2.5"><Button data-testid={`invoice-pdf-${r.id}`} size="icon" variant="ghost" className="h-8 w-8 text-slate-500 hover:text-[#0B132B]" title="View PDF" onClick={() => openPdf(r.id)}><FileText className="w-4 h-4" /></Button></td>
            </>)} />
        </TabsContent>
        <TabsContent value="orders" className="mt-4">
          <Table head={["Type", "Order No", "Date", "Product", "Qty", "Total Amount", "Status", "Progress"]} rows={d.orders} empty="No orders"
            render={(r) => (<>
              <td className="px-4 py-2.5"><Badge variant="outline" className={`rounded-sm ${r.type === "Invoice" ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-slate-100 text-slate-700 border-slate-200"}`}>{r.type}</Badge></td>
              <td className="px-4 py-2.5 font-medium text-slate-800">{r.order_no}</td>
              <td className="px-4 py-2.5 whitespace-nowrap">{fmtDate(r.order_date)}</td>
              <td className="px-4 py-2.5">{r.products}</td>
              <td className="px-4 py-2.5 font-mono tabular-nums">{r.quantity}</td>
              <td className="px-4 py-2.5 font-mono tabular-nums">{fmtINR(r.total)}</td>
              <td className="px-4 py-2.5"><Badge variant="outline" className="rounded-sm capitalize">{r.status}</Badge></td>
              <td className="px-4 py-2.5">{progressBadge(r.progress)}</td>
            </>)} />
        </TabsContent>
        <TabsContent value="payments" className="mt-4">
          <Table head={["Date", "Amount", "Mode", "Reference", "Against Invoice", "Remaining Balance"]} rows={d.payments} empty="No payments"
            render={(r) => (<>
              <td className="px-4 py-2.5 whitespace-nowrap">{fmtDate(r.payment_date)}</td>
              <td className="px-4 py-2.5 font-mono tabular-nums text-emerald-600">{fmtINR(r.amount)}</td>
              <td className="px-4 py-2.5">{r.payment_mode}</td>
              <td className="px-4 py-2.5">{r.reference_number || "-"}</td>
              <td className="px-4 py-2.5">{r.against_invoice}</td>
              <td className="px-4 py-2.5 font-mono tabular-nums">{fmtINR(r.remaining_balance)}</td>
            </>)} />
        </TabsContent>
        <TabsContent value="samples" className="mt-4">
          <Table head={["Sample No", "Date", "Product", "Batch", "Qty", "Courier", "AWB", "Status", "Remarks"]} rows={d.samples || []} empty="No samples sent"
            render={(r) => (<>
              <td className="px-4 py-2.5 font-medium text-slate-800">{r.sample_no}</td>
              <td className="px-4 py-2.5 whitespace-nowrap">{fmtDate(r.sample_date)}</td>
              <td className="px-4 py-2.5">{r.product_name}</td>
              <td className="px-4 py-2.5 font-mono">{r.batch_number}</td>
              <td className="px-4 py-2.5 font-mono tabular-nums">{r.quantity} {r.unit}</td>
              <td className="px-4 py-2.5">{r.courier}</td>
              <td className="px-4 py-2.5 font-mono">{r.tracking_number || "-"}</td>
              <td className="px-4 py-2.5"><Badge variant="outline" className="rounded-sm capitalize">{r.status}</Badge></td>
              <td className="px-4 py-2.5 text-slate-500">{r.remarks || "-"}</td>
            </>)} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
